import React, { useEffect } from "react";
import { motion } from "framer-motion";
import image1 from "../assets/images/homeimage1.avif";
import { useNavigate } from "react-router-dom";
import ScrollingText from "../components/common/ScrollingText";
import { acceleratedDevelopmentIcon, innovativeApproachIcon, growthFocusedIcon, secureReliableIcon, globalReachIcon, partnershipModelIcon } from "../components/svg/home";

// Smooth scrolling setup
const scrollToSection = (id) => {
  const element = document.getElementById(id);
  if (element) {
    element.scrollIntoView({ behavior: "smooth" });
  }
};

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.2,
      when: "beforeChildren"
    }
  }
};

const itemVariants = {
  hidden: { y: 30, opacity: 0 },
  visible: {
    y: 0,
    opacity: 1,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 12
    }
  }
};

const imageVariants = {
  hidden: { x: 100, opacity: 0, rotate: 2 },
  visible: {
    x: 0,
    opacity: 1,
    rotate: 0,
    transition: {
      type: "spring",
      stiffness: 50,
      damping: 15,
      delay: 0.4
    }
  }
};

const cardHover = {
  scale: 1.03,
  y: -8,
  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
  transition: { duration: 0.3, ease: "easeOut" }
};

const Home = () => {
  useEffect(() => {
    // Add smooth scrolling behavior to the whole page
    document.documentElement.style.scrollBehavior = "smooth";

    return () => {
      document.documentElement.style.scrollBehavior = "auto";
    };
  }, []);
  const navigate = useNavigate();
  return (
    <div className="bg-white overflow-hidden mt-10 md:mt-10 lg:mt-0">
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="mx-auto px-5 xl:px-20 2xl:px-32 py-12"
      >
        {/* Hero Section */}
        <div className="min-h-[75vh] flex items-center mb-12">
          <div className="flex flex-col lg:flex-row items-center">
            {/* Left side - Text content */}
            <motion.div
              variants={containerVariants}
              className="lg:w-1/2 mb-10 lg:mb-0 lg:pr-12"
            >
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: 80 }}
                transition={{ delay: 0.5, duration: 0.6 }}
                className="h-1.5 bg-gradient-to-r from-yellow-400 to-yellow-500 rounded-full mb-6"
              ></motion.div>

              <motion.h1
                variants={itemVariants}
                className="text-4xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight"
              >
                Transforming Ideas Into <span className="bg-gradient-to-r from-yellow-500 to-yellow-600 bg-clip-text text-transparent">Digital Reality</span>
              </motion.h1>

              <motion.p
                variants={itemVariants}
                className="text-gray-600 text-xl mb-8 max-w-2xl"
              >
                We create innovative digital solutions that drive growth and elevate brands. Our team of experts delivers cutting-edge technology services tailored to your business needs.
              </motion.p>

              <motion.div variants={itemVariants} className="flex gap-4 flex-col sm:flex-row justify-center sm:justify-start">
                <motion.button
                  whileHover={{ scale: 1.05, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.2)" }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => scrollToSection('contact')}
                  className="bg-gray-900 hover:bg-black text-white px-6 py-3 rounded-lg text-lg font-semibold shadow-lg transition-colors"
                >
                  Get Started
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.05, backgroundColor: "#f9fafb" }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => scrollToSection('solutions')}
                  className="border border-gray-200 text-gray-700 hover:bg-gray-50 px-6 py-3 rounded-lg text-lg font-medium transition-colors"
                >
                  Explore Solutions
                </motion.button>
              </motion.div>
            </motion.div>

            {/* Right side - Image */}
            <motion.div
              variants={imageVariants}
              className="lg:w-1/2 relative"
            >
              <motion.img
                src={image1}
                alt="Digital Transformation"
                className="rounded-2xl shadow-2xl w-full h-auto"
                whileHover={{ scale: 1.02, transition: { duration: 0.4 } }}
              />
              <motion.div
                className="absolute -bottom-4 -left-4 w-24 h-24 bg-yellow-400 rounded-2xl -z-10 opacity-20"
                animate={{
                  y: [0, -8, 0],
                  rotate: [0, 5, 0]
                }}
                transition={{
                  repeat: Infinity,
                  duration: 8,
                  ease: "easeInOut"
                }}
              />
              <motion.div
                className="absolute -top-4 -right-4 w-20 h-20 bg-black rounded-2xl -z-10 opacity-10"
                animate={{
                  y: [0, 8, 0],
                  rotate: [0, -5, 0]
                }}
                transition={{
                  repeat: Infinity,
                  duration: 6,
                  ease: "easeInOut",
                  delay: 0.5
                }}
              />
            </motion.div>
          </div>
        </div>

 {/* Client Logos Marquee */}
        <div className="my-16">
          <ScrollingText />
        </div>

        {/* Value Proposition Section */}
        <motion.section
          id="solutions"
          initial="hidden"
          whileInView="visible"
          variants={containerVariants}
          viewport={{ once: true, margin: "-100px" }}
          className="mb-20"
        >
          <div className="text-center max-w-3xl mx-auto mb-12">
            <motion.span
              variants={itemVariants}
              className="inline-block px-4 py-1.5 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium mb-4"
            >
              Why Choose Us
            </motion.span>
            <motion.h2
              variants={itemVariants}
              className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
            >
              Innovative Solutions for <span className="bg-gradient-to-r from-yellow-500 to-yellow-600 bg-clip-text text-transparent">Digital Growth</span>
            </motion.h2>
            <motion.p
              variants={itemVariants}
              className="text-gray-600 text-xl"
            >
              We combine cutting-edge technology with strategic thinking to deliver exceptional results for our clients.
            </motion.p>
          </div>

          <motion.div
            variants={containerVariants}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {[
              {
                icon: acceleratedDevelopmentIcon,
                title: "Accelerated Development",
                description: "Rapid prototyping and agile methodologies to bring your ideas to market faster."
              },
              {
                icon: innovativeApproachIcon,
                title: "Innovative Approach",
                description: "Creative problem-solving with the latest technologies and design thinking."
              },
              {
                icon: growthFocusedIcon,
                title: "Growth Focused",
                description: "Solutions designed to scale with your business and drive measurable results."
              },
              {
                icon: secureReliableIcon,
                title: "Secure & Reliable",
                description: "Enterprise-grade security and robust architecture for peace of mind."
              },
              {
                icon: globalReachIcon,
                title: "Global Reach",
                description: "Solutions that work across borders and devices for worldwide impact."
              },
              {
                icon: partnershipModelIcon,
                title: "Partnership Model",
                description: "We work as an extension of your team, aligned with your goals and vision."
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                whileHover={cardHover}
                className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all border border-gray-100 group relative overflow-hidden text-center"
              >
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-yellow-400 to-yellow-500"></div>
                <div className="text-4xl mb-4 flex justify-center">{item.icon}</div>
                <h3 className="text-xl font-bold mb-3 text-gray-900">{item.title}</h3>
                <p className="text-gray-600 text-sm">
                  {item.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </motion.section>

        {/* Stats Section */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          variants={containerVariants}
          viewport={{ once: true, margin: "-100px" }}
          className="mb-20 bg-gradient-to-br from-gray-900 to-black rounded-3xl p-10 text-white shadow-2xl relative overflow-hidden"
        >
          <div className="absolute top-0 right-0 w-48 h-48 bg-yellow-500 rounded-full -mt-24 -mr-24 opacity-10"></div>
          <div className="absolute bottom-0 left-0 w-36 h-36 bg-yellow-500 rounded-full -mb-18 -ml-18 opacity-10"></div>

          <motion.h2
            variants={itemVariants}
            className="text-4xl md:text-5xl font-bold text-center mb-12 relative z-10"
          >
            Our Impact in Numbers
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 relative z-10">
            {[
              { number: '150+', label: 'Projects Completed' },
              { number: '95%', label: 'Client Retention' },
              { number: '40+', label: 'Team Members' },
              { number: '5+', label: 'Years of Excellence' }
            ].map((stat, index) => (
              <motion.div
                key={index}
                variants={itemVariants}
                className="text-center p-5 bg-white/5 rounded-2xl backdrop-blur-sm border border-white/10"
              >
                <motion.p
                  className="text-5xl font-bold mb-2 bg-gradient-to-r from-yellow-400 to-yellow-500 bg-clip-text text-transparent"
                  initial={{ scale: 0 }}
                  whileInView={{ scale: 1 }}
                  transition={{ delay: index * 0.1, type: "spring", stiffness: 100 }}
                >
                  {stat.number}
                </motion.p>
                <p className="text-gray-300 text-md">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </motion.section>

        {/* Case Study Highlight */}
        <motion.section
          initial="hidden"
          whileInView="visible"
          variants={containerVariants}
          viewport={{ once: true, margin: "-100px" }}
          className="mb-20"
        >
          <div className="flex flex-col lg:flex-row items-center gap-10">
            <motion.div
              variants={itemVariants}
              className="lg:w-1/2"
            >
              <motion.span
                variants={itemVariants}
                className="inline-block px-4 py-1.5 bg-yellow-100 text-yellow-700 rounded-full text-sm font-medium mb-4"
              >
                Featured Work
              </motion.span>
              <motion.h2
                variants={itemVariants}
                className="text-4xl md:text-5xl font-bold text-gray-900 mb-6"
              >
                Revolutionizing E-commerce with AI
              </motion.h2>
              <motion.p
                variants={itemVariants}
                className="text-gray-600 text-lg mb-6"
              >
                We helped a leading retail brand transform their online presence with a custom AI-powered platform that increased conversions by 217% and reduced cart abandonment by 63%.
              </motion.p>
              <motion.div variants={itemVariants}>
                <motion.button
                  whileHover={{ scale: 1.05, boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.1)" }}
                  whileTap={{ scale: 0.98 }}
                  className="border-2 border-gray-900 text-gray-900 hover:bg-gray-900 hover:text-white px-6 py-3 rounded-lg text-lg font-semibold transition-colors"
                >
                  View Case Study
                </motion.button>
              </motion.div>
            </motion.div>

            <motion.div
              variants={itemVariants}
              className="w-full lg:w-1/2 bg-gradient-to-br from-yellow-50 to-gray-100 rounded-2xl p-4 sm:p-6 shadow-lg"
            >
              <div className="bg-white rounded-xl p-4 sm:p-5 shadow-md">
                {/* Profile Header */}
                <div className="flex items-center mb-4 sm:mb-5">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 bg-yellow-100 rounded-full flex items-center justify-center text-yellow-700 font-bold mr-3 sm:mr-4 text-sm sm:text-md">
                    SM
                  </div>
                  <div>
                    <h4 className="font-bold text-gray-900 text-sm sm:text-base md:text-lg">
                      Stebin Milton
                    </h4>
                    <p className="text-gray-500 text-xs sm:text-sm">CEO Of GEOCODE</p>
                  </div>
                </div>

                {/* Quote */}
                <p className="text-gray-600 mb-4 sm:mb-5 italic text-sm sm:text-base">
                  "The platform completely transformed our business. We saw results within the first month and continue to achieve growth milestones we never thought possible."
                </p>

                {/* Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="text-center p-3 bg-yellow-50 rounded-lg">
                    <p className="text-xl sm:text-2xl font-bold text-yellow-700">217%</p>
                    <p className="text-xs sm:text-sm text-gray-600">Increase in Conversions</p>
                  </div>
                  <div className="text-center p-3 bg-yellow-50 rounded-lg">
                    <p className="text-xl sm:text-2xl font-bold text-yellow-700">63%</p>
                    <p className="text-xs sm:text-sm text-gray-600">Reduction in Cart Abandonment</p>
                  </div>
                </div>
              </div>
            </motion.div>

          </div>
        </motion.section>

        {/* CTA Section */}
        <motion.section
          id="contact"
          initial="hidden"
          whileInView="visible"
          variants={containerVariants}
          viewport={{ once: true, margin: "-100px" }}
          className="text-center py-16 bg-gradient-to-br from-gray-900 to-black rounded-3xl shadow-2xl relative overflow-hidden"
        >
          {/* Background decorative elements */}
          <div className="absolute top-0 right-0 w-48 h-48 bg-yellow-500 rounded-full -mt-24 -mr-24 opacity-10"></div>
          <div className="absolute bottom-0 left-0 w-36 h-36 bg-yellow-500 rounded-full -mb-18 -ml-18 opacity-10"></div>

          {/* Add this container div with variants */}
          <motion.div
            variants={containerVariants}
            className="relative z-10 p-2 "
          >
            <motion.h2
              variants={itemVariants}
              className="text-4xl md:text-5xl font-bold mb-6 text-white"
            >
              Ready to Start Your Project?
            </motion.h2>
            <motion.p
              variants={itemVariants}
              className="max-w-2xl mx-auto mb-8 text-gray-300 text-xl"
            >
              Let's collaborate to bring your ideas to life with innovative solutions tailored to your needs.
            </motion.p>
            <motion.button
              variants={itemVariants}
              whileHover={{
                scale: 1.05,
                boxShadow: "0 15px 30px -5px rgba(0, 0, 0, 0.3)"
              }}
              whileTap={{ scale: 0.98 }}
              onClick={() => navigate("/contact")}
              className="bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-gray-900 px-8 py-3 rounded-lg text-lg font-semibold shadow-lg transition-all duration-300"
            >
              Contact Us Today
            </motion.button>
          </motion.div>
        </motion.section>
        
      </motion.div>
    </div>
  );
};

export default Home;